import React, { useEffect, useState, useRef } from 'react';
import { Clock, FileText, Brain, PenTool, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react';
import { useMarkingStore } from '../../stores/markingStore';

const STAGE_CONFIG = {
  uploading: { label: 'Uploading files...', icon: FileText, color: 'text-blue-500' },
  processing: { label: 'Processing documents...', icon: FileText, color: 'text-blue-500' },
  downloading: { label: 'Downloading files...', icon: FileText, color: 'text-blue-500' },
  'sending-to-ai': { label: 'Sending to AI...', icon: Brain, color: 'text-purple-500' },
  'ai-analyzing': { label: 'AI is analyzing answers...', icon: Brain, color: 'text-purple-500' },
  analyzing: { label: 'AI is analyzing answers...', icon: Brain, color: 'text-purple-500' },
  'drawing-annotations': { label: 'Drawing marks on script...', icon: PenTool, color: 'text-orange-500' },
  overlaying: { label: 'Overlaying annotations...', icon: PenTool, color: 'text-orange-500' },
  completed: { label: 'Marking complete!', icon: CheckCircle, color: 'text-green-500' },
  failed: { label: 'Marking failed', icon: AlertCircle, color: 'text-red-500' }
};

/** All stages that mean work is still in progress */
const ACTIVE_STATUSES = [
  'uploading', 'processing', 'downloading',
  'sending-to-ai', 'ai-analyzing', 'analyzing',
  'drawing-annotations', 'overlaying'
];

/** Ordered steps shown in the progress sidebar */
const STEP_ORDER = ['uploading', 'processing', 'analyzing', 'overlaying', 'completed'];

/** Map every possible status to its closest step index for progress display */
const statusToStepIndex = (s) => {
  const map = {
    uploading: 0,
    processing: 1, downloading: 1,
    'sending-to-ai': 2, 'ai-analyzing': 2, analyzing: 2,
    'drawing-annotations': 3, overlaying: 3,
    completed: 4
  };
  return map[s] ?? 1; // default to processing
};

const STALE_MS = 5 * 60 * 1000; // 5 minutes without an update → warn

const MarkingProgressTracker = ({ jobId, onRetry }) => {
  const { currentJob, subscribeToJob, loadMarkingJob } = useMarkingStore();
  const [isStale, setIsStale] = useState(false);
  const lastUpdateRef = useRef(Date.now());

  useEffect(() => {
    if (!jobId) return;
    const unsub = subscribeToJob(jobId);
    return () => unsub();
  }, [jobId, subscribeToJob]);

  // Track staleness — warn if no Firestore update for 5 minutes
  useEffect(() => {
    if (!currentJob) return;
    lastUpdateRef.current = Date.now();
    setIsStale(false);
  }, [currentJob?.status, currentJob?.progress?.stage, currentJob?.progress?.currentPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastUpdateRef.current > STALE_MS) setIsStale(true);
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  if (!currentJob) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600 mx-auto"></div>
        <p className="text-sm text-gray-500 mt-3">Loading job status...</p>
      </div>
    );
  }

  const status = currentJob.status || 'processing';
  const progress = currentJob.progress || {};
  const stage = progress.stage || status;
  const stageInfo = STAGE_CONFIG[stage] || STAGE_CONFIG['processing'];
  const StageIcon = stageInfo.icon;

  const isActive = ACTIVE_STATUSES.includes(status);
  const isFailed = status === 'failed';
  const isComplete = status === 'completed';

  const progressPercent = progress.totalPages > 0
    ? Math.round((progress.currentPage / progress.totalPages) * 100)
    : 0;

  const currentStepIdx = statusToStepIndex(status);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-4">
        {isActive && (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sigma-600"></div>
        )}
        <StageIcon className={`w-5 h-5 ${stageInfo.color}`} />
        <h3 className="text-lg font-semibold text-gray-900">
          {currentJob.title || 'Marking Job'}
        </h3>
      </div>

      <p className={`text-sm font-medium mb-4 ${stageInfo.color}`}>
        {stageInfo.label}
      </p>

      {/* Progress bar */}
      {isActive && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>
              {progress.currentPage > 0
                ? `Page ${progress.currentPage} of ${progress.totalPages}`
                : 'Preparing...'}
            </span>
            {progressPercent > 0 && <span>{progressPercent}%</span>}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-sigma-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progressPercent || (isActive ? 10 : 0)}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Status steps */}
      <div className="space-y-2 mt-4">
        {STEP_ORDER.map((step, idx) => {
          const isDone = idx < currentStepIdx || (idx === currentStepIdx && isComplete);
          const isCurrent = idx === currentStepIdx && !isComplete && !isFailed;

          return (
            <div key={step} className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                isDone ? 'bg-green-500' : isCurrent ? 'bg-sigma-500 animate-pulse' : 'bg-gray-300'
              }`} />
              <span className={`text-xs ${
                isDone ? 'text-green-600' : isCurrent ? 'text-sigma-600 font-medium' : 'text-gray-400'
              }`}>
                {STAGE_CONFIG[step]?.label || step}
              </span>
            </div>
          );
        })}
      </div>

      {isFailed && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{currentJob.error || 'An unknown error occurred.'}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 inline-flex items-center text-xs font-medium text-red-700 hover:text-red-900"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Retry
            </button>
          )}
        </div>
      )}

      {isStale && isActive && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-700">
            No updates received for a while. The job may have stalled.
          </p>
          <button
            onClick={() => loadMarkingJob(jobId)}
            className="mt-1 inline-flex items-center text-xs font-medium text-amber-700 hover:text-amber-900"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> Refresh status
          </button>
        </div>
      )}

      {isActive && !isStale && (
        <div className="mt-4 flex items-center text-xs text-gray-400">
          <Clock className="w-3 h-3 mr-1" />
          This usually takes 30-90 seconds
        </div>
      )}
    </div>
  );
};

export default MarkingProgressTracker;
